/**
 * Shell de navegación del POS web. Sidebar con items filtrados por
 * permiso del rol — un waiter sin `cash.openOwn` no ve "Caja" aunque
 * sepa la URL; el backend también lo corta con 403 (defensa en profundidad).
 */

import { Button, ThemeToggle } from '@atreus/ui';
import { Link, useLocation } from '@tanstack/react-router';
import {
  Archive,
  BarChart3,
  Coins,
  ConciergeBell,
  Contact,
  LayoutGrid,
  LogOut,
  Menu,
  Package,
  Receipt,
  Settings,
  Tag,
  UtensilsCrossed,
  X,
} from 'lucide-react';

import { useEffect, useState, type ComponentType, type ReactNode } from 'react';

import type { FeatureId, PermissionId } from '@atreus/core';

import { clearSession, loadSession } from '../auth/session.js';
import { useFeatures } from '../lib/features.js';
import { usePermissions } from '../lib/permissions.js';
import { trpc } from '../lib/trpc.js';
import { resolveImageUrl } from './ImageDropzone.js';

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** Si se declara, el ítem solo aparece cuando el usuario tiene el permiso. */
  requiresPermission?: PermissionId;
  /** Si se declara, el ítem solo aparece cuando la feature está activa. */
  requiresFeature?: FeatureId;
}

const NAV: readonly NavItem[] = [
  { to: '/', label: 'Inicio', icon: LayoutGrid },
  {
    to: '/categories',
    label: 'Categorías',
    icon: Tag,
    requiresPermission: 'categories.view',
  },
  {
    to: '/products',
    label: 'Productos',
    icon: Package,
    requiresPermission: 'products.view',
  },
  {
    to: '/inventory',
    label: 'Inventario',
    icon: Archive,
    requiresPermission: 'inventory.view',
    requiresFeature: 'inventory',
  },
  {
    to: '/orders',
    label: 'Pedidos',
    icon: UtensilsCrossed,
    requiresPermission: 'orders.view',
  },
  {
    to: '/tables',
    label: 'Mesas',
    icon: ConciergeBell,
    requiresPermission: 'tables.view',
    requiresFeature: 'tables',
  },
  {
    to: '/cash',
    label: 'Caja',
    icon: Coins,
    requiresPermission: 'cash.openOwn',
  },
  {
    to: '/customers',
    label: 'Clientes',
    icon: Contact,
    requiresPermission: 'customers.view',
    requiresFeature: 'customer_credit',
  },
  {
    to: '/stats',
    label: 'Estadísticas',
    icon: BarChart3,
    requiresPermission: 'stats.view',
    requiresFeature: 'stats',
  },
  {
    to: '/expenses',
    label: 'Gastos',
    icon: Receipt,
    requiresPermission: 'expenses.view',
    requiresFeature: 'expenses',
  },
  {
    to: '/settings',
    label: 'Configuraciones',
    icon: Settings,
    requiresPermission: 'settings.view',
  },
];

/** Evita re-prefetchear en cada re-mount del AppShell (que no debería pasar,
 * pero vitest o HMR pueden remountearlo). La primera instancia que corra
 * el efecto hace el prefetch; las demás lo skipean. */
let prefetchStarted = false;

export const AppShell = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const session = loadSession();
  const permissions = usePermissions();
  const utils = trpc.useUtils();
  // Branding del tenant — solo el logo, opcional. Si la query aún no
  // resolvió o no hay logo, no renderizamos nada en el slot (no hay
  // skeleton porque es chico y caería raro al lado del logo de Atreus).
  const tenantQuery = trpc.tenant.current.useQuery(undefined, {
    enabled: !!session,
    staleTime: 60_000,
  });
  const tenantLogoUrl = resolveImageUrl(
    tenantQuery.data?.tenant.logoUrl ?? null,
  );

  // Prefetch "hot path" post-login: al mount del shell disparamos las
  // queries más probables en paralelo con la primera ruta. Cuando el
  // usuario navega a /products o /orders la data ya está en cache y la
  // transición se siente instantánea.
  useEffect(() => {
    if (prefetchStarted || !session) return;
    prefetchStarted = true;
    void utils.categories.list.prefetch();
    void utils.products.list.prefetchInfinite({ limit: 100 });
    void utils.products.count.prefetch({ activeOnly: false });
    void utils.orders.count.prefetch({ activeOnly: true });
  }, [utils, session]);

  const features = useFeatures();
  const navItems = NAV.filter((it) => {
    if (it.requiresPermission && !permissions.canDo(it.requiresPermission))
      return false;
    if (it.requiresFeature && !features.isEnabled(it.requiresFeature))
      return false;
    return true;
  });

  const logout = (): void => {
    clearSession();
    window.location.reload();
  };

  // Drawer del sidebar para mobile (< md). En desktop el sidebar está
  // estático y este estado se ignora. Auto-cierra al cambiar de ruta para
  // que tras tocar un link el contenido sea visible sin tocar fuera.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-screen flex-col bg-surface text-fg">
      <header className="atreus-app-shell-chrome flex items-center justify-between gap-3 border-b border-border px-4 py-3 md:px-6">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="-ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-fg hover:bg-surface-muted md:hidden"
            aria-label="Abrir menú de navegación"
          >
            <Menu className="size-5" />
          </button>
          <img
            src="/atreus-logo.svg"
            alt="Atreus"
            className="h-7 w-auto shrink-0 md:h-8 dark:invert"
          />
          <div className="flex min-w-0 items-center gap-2 border-l border-border pl-3 text-xs text-fg-muted">
            {tenantLogoUrl ? (
              <img
                src={tenantLogoUrl}
                alt={session?.tenantName ?? 'Logo del negocio'}
                className="h-7 w-auto shrink-0 md:h-8"
              />
            ) : null}
            <span className="block truncate">
              {session?.tenantName ?? 'POS'}
            </span>
          </div>
        </div>
        <ThemeToggle />
      </header>

      <div className="relative flex flex-1 overflow-hidden">
        {/* Backdrop del drawer mobile — click cierra. md:hidden quita el
            overlay en desktop donde el sidebar está estático. */}
        {mobileNavOpen ? (
          <button
            type="button"
            aria-label="Cerrar menú"
            onClick={() => setMobileNavOpen(false)}
            className="fixed inset-0 z-40 bg-fg/50 md:hidden"
          />
        ) : null}

        <nav
          className={
            'atreus-app-shell-chrome z-50 flex w-64 shrink-0 flex-col justify-between border-r border-border px-3 py-4 transition-transform duration-200 ease-out ' +
            // En mobile el drawer flota sobre el contenido → necesita
            // fondo opaco (`bg-surface`). En desktop está al lado del
            // contenido y un tinte sutil (`bg-surface-muted/30`) lo
            // diferencia del main sin contraste excesivo.
            'bg-surface shadow-xl md:bg-surface-muted/30 md:shadow-none ' +
            'fixed inset-y-0 left-0 md:static md:w-56 md:translate-x-0 ' +
            (mobileNavOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0')
          }
          aria-label="Navegación principal"
        >
          {/* Botón cerrar visible solo dentro del drawer mobile. */}
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            className="mb-2 ml-auto flex h-9 w-9 items-center justify-center rounded-md text-fg-muted hover:bg-surface hover:text-fg md:hidden"
            aria-label="Cerrar menú"
          >
            <X className="size-5" />
          </button>

          <ul className="flex-1 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const active = location.pathname === item.to;
              const Icon = item.icon;
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold transition-colors ' +
                      (active
                        ? 'bg-fg text-fg-inverse'
                        : 'text-fg-muted hover:bg-surface hover:text-fg')
                    }
                    aria-current={active ? 'page' : undefined}
                  >
                    <Icon className="size-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="space-y-1 border-t border-border pt-3 text-xs">
            {session ? (
              <div className="px-3 py-1 text-fg-muted">
                <span className="block truncate">{session.user.fullName}</span>
                <span className="mt-0.5 block truncate text-[10px]">
                  {session.user.email}
                </span>
              </div>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start"
              onClick={logout}
            >
              <LogOut className="size-4" />
              Cerrar sesión
            </Button>
          </div>
        </nav>

        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
};
